import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TpETLObjectTypes, TpMatrixColumnType, TpETLTableColumn } from '../../etl-data-types/interfaces';
import { TpETLComponentTypeManifest } from '../interfaces';
import { TpETLPipelineComponent, TpETLDryRun, TpETLDryRunComponentResult } from '../../interfaces';
import { getETLComponentInputObjectMatrix, addDryRunObjectMatrix } from '../../utils';
import { getDryRunErrorsForComponent } from '../../etlDryRun';
import { copyETLDryRunObjectMatrix } from '../utils';
import styles from '../../edit-etl-component/editETLComponentForm.module.scss';
import ParamSet from '../../edit-etl-component/ParamSet';
import { FancyButton } from '../../../../components/primitives/FancyButton';
import { get_guid } from '../../../visual-analytics/helpers/misc';
import { TpFilterInstance, TpMatrixFilterInstance } from '../../filters/interfaces';
import {
  getDefaultFilterType,
  filterHasCompatibleSourceColumn,
  filterTypesList,
  getFilterTypeInfo,
} from '../../filters/filterTypes';

function performDryRun(dryRun: TpETLDryRun, comp: TpETLPipelineComponent): TpETLDryRunComponentResult {
  const matrix = addDryRunObjectMatrix(dryRun, comp.outputObjectIds['result'], comp);
  const input = getETLComponentInputObjectMatrix(dryRun, comp, 'source');
  copyETLDryRunObjectMatrix(input, matrix);

  // Check filters for validity
  const filters = (comp.params['filters'] || []) as TpMatrixFilterInstance[];
  filters.forEach((filter) => {
    const filterType = getFilterTypeInfo(filter.filterType);
    filterType.performDryRun({ dryRun, comp, filter });
  });

  return {
    abort: false,
  };
}

function renderCustomConfigParams(
  dryRun: TpETLDryRun,
  comp: TpETLPipelineComponent,
  updateEditingComponent: (newComp: TpETLPipelineComponent) => void,
): any {
  const sourceMatrix = getETLComponentInputObjectMatrix(dryRun, comp, 'source');
  const filters = (comp.params['filters'] || []) as TpMatrixFilterInstance[];

  let defaultDimension: TpMatrixColumnType = 'observations';
  let defaultCol: TpETLTableColumn;
  if (sourceMatrix.obsMetadataColumns.length > 0) {
    defaultCol = sourceMatrix.obsMetadataColumns[0];
  } else if (sourceMatrix.featureMetadataColumns.length > 0) {
    defaultCol = sourceMatrix.featureMetadataColumns[0];
    defaultDimension = 'features';
  } else {
    return null;
  }

  function updateFilterParam(filterId: string, paramId: string, newValue: any) {
    const filter = filters.find((f) => f.id === filterId);
    if (filter === undefined) throw `Invalid filter id ${filterId}`;
    if (paramId == 'colId') {
      const [diminesion, colId] = newValue.split('$');
      filter.dimension = diminesion as TpMatrixColumnType | '';
      let colInfo: TpETLTableColumn | undefined;
      if (diminesion === 'observations') {
        colInfo = sourceMatrix.obsMetadataColumns.find((col) => col.identifier === colId);
      } else {
        colInfo = sourceMatrix.featureMetadataColumns.find((col) => col.identifier === colId);
      }
      filter.colId = colId ?? '';
      filter.value = '';
      filter.valueType = colInfo?.dataType ?? '';
      if (colInfo) {
        const currentFilterType = getFilterTypeInfo(filter.filterType);
        if (!filterHasCompatibleSourceColumn(currentFilterType.id, colInfo.dataType)) {
          filter.filterType = getDefaultFilterType(colInfo.dataType).id;
        }
      }
    } else if (paramId == 'filterType') {
      filter.filterType = newValue;
      filter.value = '';
    } else if (paramId == 'value') {
      filter.value = newValue;
    }
    updateEditingComponent(comp);
  }

  function renderFilterErrors(filter: TpFilterInstance) {
    const errors = getDryRunErrorsForComponent(dryRun, comp.id).filter((error) => error.paramId == filter.id);
    if (errors.length == 0) return null;
    return (
      <div className={styles.errors}>
        {errors.map((error, idx) => (
          <div key={`errors_${filter.id}_${idx}`}>{error.message}</div>
        ))}
      </div>
    );
  }

  return (
    <>
      {filters.map((filter) => (
        <div key={`filter_${filter.id}`} className={styles.section}>
          <h3>
            Filter
            <button
              onClick={() => {
                comp.params['filters'] = filters.filter((f) => filter.id !== f.id);
                updateEditingComponent(comp);
              }}
            >
              <FontAwesomeIcon icon="xmark" />
            </button>
          </h3>
          {renderFilterErrors(filter)}

          <ParamSet
            params={[
              {
                name: 'Source column',
                component: (
                  <select
                    value={filter.dimension ? `${filter.dimension}$${filter.colId}` : ''}
                    onChange={(ev) => {
                      updateFilterParam(filter.id, 'colId', ev.target.value);
                    }}
                  >
                    <option key="_empty_" value={''}>
                      - Select -
                    </option>
                    <optgroup label="Observations">
                      {sourceMatrix.obsMetadataColumns.map((col) => (
                        <option key={col.identifier} value={`observations$${col.identifier}`}>
                          {col.name}
                        </option>
                      ))}
                    </optgroup>
                    <optgroup label="Features">
                      {sourceMatrix.featureMetadataColumns.map((col) => (
                        <option key={col.identifier} value={`features$${col.identifier}`}>
                          {col.name}
                        </option>
                      ))}
                    </optgroup>
                  </select>
                ),
              },
              {
                name: 'Filter type',
                component: (
                  <>
                    <select
                      value={filter.filterType}
                      onChange={(ev) => {
                        updateFilterParam(filter.id, 'filterType', ev.target.value);
                      }}
                    >
                      {filterTypesList
                        .filter((filterType) => filterHasCompatibleSourceColumn(filterType.id, filter.valueType))
                        .map((aggType) => (
                          <option key={aggType.id} value={aggType.id}>
                            {aggType.name}
                          </option>
                        ))}
                    </select>
                    <div style={{ marginTop: '3px' }}>
                      {filter.filterType && getFilterTypeInfo(filter.filterType).description}
                    </div>
                  </>
                ),
              },
              {
                name: 'Value',
                component: (
                  <input
                    type="text"
                    value={filter.value}
                    onChange={(ev) => {
                      updateFilterParam(filter.id, 'value', ev.target.value);
                    }}
                  />
                ),
              },
            ]}
          />
        </div>
      ))}

      <FancyButton
        iconName="fas:plus"
        onClick={() => {
          const newFilter: TpMatrixFilterInstance = {
            id: get_guid(),
            dimension: defaultDimension,
            colId: defaultCol.identifier,
            filterType: getDefaultFilterType(defaultCol.dataType).id,
            value: '',
            valueType: defaultCol.dataType,
          };
          comp.params['filters'] = [...filters, newFilter];
          updateEditingComponent(comp);
        }}
      >
        Add Filter
      </FancyButton>
    </>
  );
}

export const manifestETLFilterMatrix: TpETLComponentTypeManifest = {
  id: 'FilterMatrix',
  name: 'Filter matrix',
  description: 'Only retains those row or columns in the matrix that fulfill a condition.',
  categoryId: 'matrix',
  icon: <FontAwesomeIcon icon={['fas', 'filter']} />,
  performDryRun,
  inputs: [
    {
      id: 'source',
      name: 'Source matrix',
      type: TpETLObjectTypes.MATRIX,
      description: 'Input matrix.',
      obsMetadataColumns: [],
      featureMetadataColumns: [],
    },
  ],
  outputs: [
    {
      type: TpETLObjectTypes.MATRIX,
      id: 'result',
      name: 'Result matrix',
      description: 'Matrix with filtered data.',
      obsMetadataColumns: [],
      featureMetadataColumns: [],
    },
  ],
  params: [],
  renderCustomConfigParams,
};
